import type { ShortsFilters } from '~/core/entities/property/filters';
import type { LocationSlugResolution } from '~~/core/ports/repositories/PropertiesRepository';
import type { Ref } from 'vue';

import {
	useRouteQueryArray,
	useRouteQueryNumber,
	useSyncFiltersToRoute
} from '~/composables/useRouteQueryHelpers';
import { ShortsFiltersPresenter } from '~/composables/useShortsFilters/ShortsFiltersPresenter';
import { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import { setRefsValue, toArray } from '~~/utils';
import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';

const NUMBER_QUERY_CONFIG = [
	{ key: 'minAskingPrice', name: 'minAskingPrice' },
	{ key: 'maxAskingPrice', name: 'maxAskingPrice' },
	{ key: 'minArea', name: 'minArea' },
	{ key: 'maxArea', name: 'maxArea' }
] as const;

/** Pinia store: Shorts filters bound to the route (query). Not suggest-specific. */
export const useShortsFiltersStore = defineStore('shortsFilters', () => {
	// 1. URL Synced State (Active)
	const search = ref('');

	// location fields são populados via slug do path (não sincronizados com query)
	const cities = ref<string[]>([]);
	const regions = ref<string[]>([]);
	const states = ref<string[]>([]);
	const macroregions = useRouteQueryArray('macroregions');

	const numberRefs = Object.fromEntries(
		NUMBER_QUERY_CONFIG.map(({ key, name }) => [name, useRouteQueryNumber(key)])
	) as {
		[K in (typeof NUMBER_QUERY_CONFIG)[number]['name']]: Ref<number | null>;
	};
	const { minAskingPrice, maxAskingPrice, minArea, maxArea } = numberRefs;

	// Sincroniza filtros numéricos (query params) com a URL
	useSyncFiltersToRoute({ minAskingPrice, maxAskingPrice, minArea, maxArea });

	// propertyTypes também vem do path (segundo segmento)
	const propertyTypes = ref<PropertyTypeEnum[]>([]);

	// 2. Local Sidebar State (Draft)
	const local = reactive({
		minAskingPrice: null as number | null,
		maxAskingPrice: null as number | null,
		minArea: null as number | null,
		maxArea: null as number | null,
		propertyTypes: [] as PropertyTypeEnum[]
	});

	const hasActiveFilters = computed(() => {
		const filterValues = [
			cities.value,
			regions.value,
			macroregions.value,
			propertyTypes.value,
			minAskingPrice.value,
			maxAskingPrice.value,
			minArea.value,
			maxArea.value
		];
		return (
			filterValues.some(ShortsFiltersPresenter.isValueApplied) || !!search.value
		);
	});

	const currentFiltersObject = computed(() => ({
		cities: toArray(cities.value),
		regions: toArray(regions.value),
		macroregions: toArray(macroregions.value),
		states: toArray(states.value)
	}));

	const arrayFilterRefs = [
		cities,
		regions,
		macroregions,
		states,
		propertyTypes
	];
	const optionalNumberRefs = [minAskingPrice, maxAskingPrice, minArea, maxArea];

	const clearFilters = () => {
		search.value = '';
		setRefsValue(arrayFilterRefs, []);
		setRefsValue(optionalNumberRefs, null);
	};

	function resetLocationFilterArrays() {
		cities.value = [];
		regions.value = [];
		macroregions.value = [];
		states.value = [];
	}

	function clearPathLocation() {
		cities.value = [];
		regions.value = [];
		states.value = [];
	}

	function applyLocationResolution(data: LocationSlugResolution | null) {
		if (data?.found) {
			cities.value = data.city ? [data.city] : [];
			regions.value = data.region ? [data.region] : [];
			states.value = data.state ? [data.state] : [];
			// macroregion só é setado se não vier no query param da URL
			if (data.macroregion && !macroregions.value.length) {
				macroregions.value = [data.macroregion];
			}
		} else {
			clearPathLocation();
		}
	}

	const sidebarNumericRefs = {
		minAskingPrice,
		maxAskingPrice,
		minArea,
		maxArea
	};
	const sidebarNumericKeys = Object.keys(
		sidebarNumericRefs
	) as (keyof typeof sidebarNumericRefs)[];

	const syncLocalFromQuery = () => {
		sidebarNumericKeys.forEach(key => {
			local[key] = sidebarNumericRefs[key].value ?? null;
		});
		local.propertyTypes = [...propertyTypes.value];
	};

	const applySidebarFilters = () => {
		sidebarNumericKeys.forEach(key => {
			sidebarNumericRefs[key].value = ShortsFiltersPresenter.sanitizeNumeric(
				local[key]
			);
		});
		propertyTypes.value = [...local.propertyTypes];
	};

	function getActiveSnapshot() {
		return {
			minAskingPrice: minAskingPrice.value,
			maxAskingPrice: maxAskingPrice.value,
			minArea: minArea.value,
			maxArea: maxArea.value,
			propertyTypes: propertyTypes.value,
			cities: cities.value,
			regions: regions.value,
			macroregions: macroregions.value
		};
	}

	function getLocalOverrides() {
		return {
			minAskingPrice: local.minAskingPrice,
			maxAskingPrice: local.maxAskingPrice,
			minArea: local.minArea,
			maxArea: local.maxArea,
			propertyTypes: local.propertyTypes
		};
	}

	const toShortsFilters = (): ShortsFilters =>
		ShortsFiltersPresenter.toShortsFilters(getActiveSnapshot());

	const toPreviewFilters = (): ShortsFilters =>
		ShortsFiltersPresenter.toShortsFilters({
			...getActiveSnapshot(),
			...getLocalOverrides()
		});

	return {
		search,
		cities,
		regions,
		macroregions,
		states,
		minAskingPrice,
		maxAskingPrice,
		minArea,
		maxArea,
		propertyTypes,
		local,
		hasActiveFilters,
		currentFiltersObject,
		clearFilters,
		resetLocationFilterArrays,
		clearPathLocation,
		applyLocationResolution,
		syncLocalFromQuery,
		applySidebarFilters,
		toShortsFilters,
		toPreviewFilters
	};
});
